/**
 * Aba "Mais" — a pessoa, as ferramentas da viagem e o beta (decisão do PO,
 * 17/09/2026, lote de aparência 1).
 *
 * Três blocos, nesta ordem: (1) cartão de perfil — foto, nome, viagem ativa e
 * as portas da conta (Perfil · Configurações da viagem · Minhas viagens ·
 * Sair); (2) ferramentas — Mapa e Memórias vieram da barra para cá, com
 * Bagagem, Enquetes, Ranking, Grupo, Cadê o carro, Emergência e Passaporte;
 * (3) beta — Enviar feedback e "Sobre o beta" em acordeão.
 *
 * Financeiro saiu daqui: ganhou aba. `/perfil` continua existindo (nome,
 * tema, senha); o cartão é a porta.
 */
import { featureEnabled } from '@nv/core';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuthStore } from '../../auth/store';
import { ReleaseDoApp } from '../../observability/ReleaseDoApp';
import { useSyncStore } from '../../sync/store';
import { useTrip } from '../../trip/TripContext';
import { Icon, type IconName } from '../../ui/icons';
import { Ilustracao, type NomeIlustracao } from '../../ui/Ilustracao';
import { Card, SectionLabel } from '../../ui/primitives';
import { FeedbackSheet } from '../feedback/FeedbackSheet';
import { FotoAvatar, useMeuAvatar } from '../perfil/FotoAvatar';

interface Ferramenta {
  para: string;
  rotulo: string;
  icone: IconName;
  /** Refino final: cada ferramenta tem o seu desenho no lugar do ícone solto. */
  ilustracao: NomeIlustracao;
  feature?: Parameters<typeof featureEnabled>[1];
  testId?: string;
}

const FERRAMENTAS: Ferramenta[] = [
  { para: '/sugestoes', rotulo: 'Sugestões', icone: 'externo', ilustracao: 'sugestao', testId: 'mais-sugestoes' },
  { para: '/mapa', rotulo: 'Mapa', icone: 'mapa', ilustracao: 'mapa', testId: 'mais-mapa' },
  { para: '/memorias', rotulo: 'Memórias', icone: 'memorias', ilustracao: 'memorias', testId: 'mais-memorias' },
  { para: '/bagagem', rotulo: 'Bagagem', icone: 'mala', ilustracao: 'bagagem', feature: 'bagagem' },
  { para: '/enquetes', rotulo: 'Enquetes', icone: 'enquete', ilustracao: 'enquetes', feature: 'enquetes', testId: 'mais-enquetes' },
  { para: '/ranking', rotulo: 'Ranking', icone: 'trofeu', ilustracao: 'ranking', feature: 'ranking', testId: 'mais-ranking' },
  { para: '/grupo', rotulo: 'Grupo e famílias', icone: 'pessoas', ilustracao: 'grupo' },
  { para: '/carro', rotulo: 'Cadê o carro', icone: 'carro', ilustracao: 'carro', feature: 'carro', testId: 'mais-carro' },
  { para: '/emergencia', rotulo: 'Cartão de emergência', icone: 'aviso', ilustracao: 'emergencia', testId: 'mais-emergencia' },
  { para: '/passaporte', rotulo: 'Passaporte', icone: 'carimbo', ilustracao: 'passaporte', feature: 'carimbos', testId: 'mais-passaporte' },
];

export function MaisScreen() {
  // `/mais?feedback=1` abre a folha direto — é para onde a tela de erro manda.
  const [busca] = useSearchParams();
  const [feedback, setFeedback] = useState(busca.get('feedback') === '1');
  const [sobreAberto, setSobreAberto] = useState(false);
  // O cartão da pessoa abre em acordeão, como o "Sobre o beta" (PO, 17/09):
  // a cabeça (foto, nome, viagem) fica sempre à vista; as portas da conta
  // aparecem ao tocar.
  const [contaAberta, setContaAberta] = useState(false);
  const { trip } = useTrip();
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const pendentes = useSyncStore((s) => s.pending);
  const meuAvatar = useMeuAvatar();

  return (
    <section className="screen mais">
      <h1>Mais</h1>

      {/* 1. A pessoa */}
      <Card className="mais-perfil" data-testid="mais-perfil-card">
        <button
          type="button"
          className="conta__cab mais-perfil__cab"
          aria-expanded={contaAberta}
          aria-controls="mais-conta"
          onClick={() => setContaAberta((v) => !v)}
          data-testid="mais-conta-toggle"
        >
          <FotoAvatar nome={user?.name ?? '?'} url={meuAvatar.data?.url} tamanho={56} className="conta__avatar" />
          <span className="conta__quem">
            <span className="mais-nome">{user?.name ?? '—'}</span>
            <span className="mais-email">{trip.name}</span>
          </span>
          <span className={contaAberta ? 'mais-beta__seta mais-beta__seta--aberta' : 'mais-beta__seta'} aria-hidden="true">
            <Icon name="avancar" size={18} />
          </span>
        </button>
        {contaAberta && (
        <div className="conta__lista" id="mais-conta">
          <Link className="conta__item" to="/perfil" data-testid="mais-perfil">
            <span className="conta__item-icone"><Icon name="pessoas" size={18} /></span>
            <span className="rk__linha-texto"><span className="rk__linha-nome">Perfil</span><span className="rk__linha-familia">Nome, tema e senha</span></span>
            <Icon name="avancar" size={18} />
          </Link>
          <Link className="conta__item" to="/viagem/configuracoes" data-testid="mais-configuracoes">
            <span className="conta__item-icone"><Icon name="configuracoes" size={18} /></span>
            <span className="rk__linha-texto"><span className="rk__linha-nome">Configurações da viagem</span></span>
            <Icon name="avancar" size={18} />
          </Link>
          <Link className="conta__item" to="/viagens" data-testid="mais-viagens">
            <span className="conta__item-icone"><Icon name="roteiro" size={18} /></span>
            <span className="rk__linha-texto"><span className="rk__linha-nome">Minhas viagens</span></span>
            <Icon name="avancar" size={18} />
          </Link>
          <button type="button" className="conta__item conta__item--sair" onClick={() => void signOut()} data-testid="mais-sair">
            <span className="conta__item-icone"><Icon name="sair" size={18} /></span>
            <span className="rk__linha-texto">
              <span className="rk__linha-nome">Sair da conta</span>
              <span className="rk__linha-familia">
                {pendentes > 0
                  ? `${pendentes} ${pendentes === 1 ? 'alteração ainda não enviada' : 'alterações ainda não enviadas'} — ao sair, você escolhe`
                  : 'Tudo sincronizado'}
              </span>
            </span>
          </button>
        </div>
        )}
      </Card>

      {/* 2. As ferramentas da viagem — grade de botões grandes, área de toque generosa. */}
      <SectionLabel>Ferramentas</SectionLabel>
      <div className="mais-grade">
        {FERRAMENTAS.filter((f) => !f.feature || featureEnabled(trip.features, f.feature)).map((f, i) => (
          <Link key={f.para} className="mais-tile nv-enter" style={{ ['--i' as string]: i }} to={f.para} data-testid={f.testId}>
            <Ilustracao nome={f.ilustracao} tamanho={56} />
            <span>{f.rotulo}</span>
          </Link>
        ))}
      </div>

      {/* 3. O beta: feedback e transparência (rodada 2B2) — a nota da
          telemetria fica num acordeão, com a release legível (rodada 2C). */}
      <SectionLabel>Beta</SectionLabel>
      <Card className="mais-beta">
        <button type="button" className="conta__item" onClick={() => setFeedback(true)} data-testid="mais-feedback">
          <span className="conta__item-icone"><Icon name="feedback" size={18} /></span>
          <span className="rk__linha-texto"><span className="rk__linha-nome">Enviar feedback</span><span className="rk__linha-familia">Bug, ideia, melhoria</span></span>
          <Icon name="avancar" size={18} />
        </button>
        <button
          type="button"
          className="conta__item"
          aria-expanded={sobreAberto}
          aria-controls="sobre-o-beta"
          onClick={() => setSobreAberto((v) => !v)}
          data-testid="mais-sobre"
        >
          <span className="conta__item-icone"><Icon name="destaque" size={18} /></span>
          <span className="rk__linha-texto"><span className="rk__linha-nome">Sobre o beta</span></span>
          <span className={sobreAberto ? 'mais-beta__seta mais-beta__seta--aberta' : 'mais-beta__seta'} aria-hidden="true">
            <Icon name="avancar" size={18} />
          </span>
        </button>
        {sobreAberto && (
          <div id="sobre-o-beta" className="mais-beta__corpo">
            <p className="auth-note" data-testid="nota-telemetria">
              Durante o beta, o Viajandos registra somente quais áreas do app foram abertas e se o uso
              ocorreu instalado ou offline. Não registramos conteúdo, valores, localização, mensagens,
              fotos ou IP para essa análise.
            </p>
            <ReleaseDoApp />
          </div>
        )}
      </Card>
      {feedback && <FeedbackSheet tripId={trip.id} onFechar={() => setFeedback(false)} />}
    </section>
  );
}
